import {
  PlayerHasMatchType,
  StepCheck,
} from '../pages/Book/MemoryBank/memoryBankTypes';
import {
  AchievementRewardType,
  IAchievementGetDTO,
  IAchievementReward,
} from '../../dorian-shared/types/achievement/Achievement';

/**
 * Reward types excluded from PlayerHas checks (bonus episodes are managed
 * separately, not via the achievements ownership model). Kept in sync with the
 * authoring UI in PlayerHasAchievementForm so the emulator prompt names exactly
 * the rewards the author was allowed to pick.
 */
const EXCLUDED_REWARD_TYPES = new Set<AchievementRewardType>([
  AchievementRewardType.Episode,
]);

function isRewardValidForPlayerHas(
  reward: IAchievementReward,
): reward is IAchievementReward & { id: number; name: string; type: AchievementRewardType } {
  if (!reward.id || !reward.name) {
    return false;
  }
  if (reward.type === undefined) {
    return false;
  }
  return !EXCLUDED_REWARD_TYPES.has(reward.type);
}

/**
 * Flatten every reward across all achievements once, keyed by reward id.
 * Rewards can be linked to multiple achievements, so the map dedupes by id.
 */
function buildRewardNameById(achievements: IAchievementGetDTO[]): Map<number, string> {
  const byId = new Map<number, string>();
  achievements.forEach((achievement) => {
    achievement.rewards?.forEach((reward) => {
      if (isRewardValidForPlayerHas(reward) && !byId.has(reward.id)) {
        // Mirror the authoring dropdown label transform for visual parity.
        byId.set(reward.id, reward.name.replace(/^Unlocked: /, '🔓 '));
      }
    });
  });
  return byId;
}

/**
 * Resolve display names for the given reward ids. Unknown ids are skipped — the
 * caller falls back to a generic prompt when nothing resolves (e.g. achievements
 * not loaded yet, or a reward was deleted after the check was authored).
 */
export function getPlayerHasRewardNames(
  achievements: IAchievementGetDTO[],
  rewardIds: number[],
): string[] {
  const byId = buildRewardNameById(achievements);
  const names: string[] = [];
  rewardIds.forEach((id) => {
    const name = byId.get(Number(id));
    if (name !== undefined) {
      names.push(name);
    }
  });
  return names;
}

/**
 * Extract the reward ids referenced by a PlayerHas check. For PlayerHas,
 * check.value is a number[] of reward ids (StepCheck doc); any other shape
 * yields an empty list and a generic prompt.
 */
export function getPlayerHasRewardIds(check: StepCheck): number[] {
  if (!Array.isArray(check.value)) {
    return [];
  }
  return check.value.map(Number).filter((id) => !Number.isNaN(id));
}

/**
 * Build the Yes/No prompt shown in the emulator when the author reaches a
 * PlayerHas check. The emulator has no real "player owns reward" state, so the
 * author answers directly; naming the rewards (and the All/Any match mode) makes
 * that answer unambiguous. Falls back to a generic question when no reward name
 * resolves.
 */
export function buildPlayerHasPrompt(
  check: StepCheck,
  achievements: IAchievementGetDTO[],
): string {
  const names = getPlayerHasRewardNames(achievements, getPlayerHasRewardIds(check));

  if (names.length === 0) {
    return 'Does the player have the required reward(s)?';
  }
  if (names.length === 1) {
    return `Player has ${names[0]}?`;
  }
  const joined = names.join(', ');
  const matchAny = check.match === PlayerHasMatchType.Any;
  return matchAny
    ? `Player has any of: ${joined}?`
    : `Player has all of: ${joined}?`;
}
